import { vec2 } from 'gl-matrix';
import { View } from '../view/View';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from './EventHandlerResult';
import { ModifierKeys } from './ModifierKeys';
import { Tool } from './Tool';
import { ViewManager } from '../view/ViewManager';
import { PlaneView } from '../view/views/PlaneView';
import { EventEmitter } from 'events';
import { LineDrawTool } from './tools/contouring-tools/LineDrawTool';
import { PanTool } from './tools/PanTool';
import { WindowingTool } from './tools/WindowingTool';
import { StaticBrushTool } from './tools/contouring-tools/StaticBrushTool';
import { AdaptiveBrushTool } from './tools/contouring-tools/AdaptiveBrushTool';
import { RegionOfInterest } from '../data/structure/RegionOfInterest';
import { ImageObject } from '../data/image/ImageObject';
import { PanZoom } from '../view/PanZoom';
import { BrushOptions, LineOptions } from '~/state/atoms/toolsAtoms';
/**
 * {@link ToolManager} is responsible for handling the user actions.
 *
 * {@link HTMLCanvasElement Canvas} catches the action user performs in the form of an event.\
 * It then passes that event to {@link ToolManager}.
 *
 * {@link ToolManager} then propagates that event through the cycle of possible {@link EventHandler EventHandlers}.\
 * The event is propagated in the following order:\
 * {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} and
 * {@link ToolManager.activeView activeView}. In case of {@link ToolManager.onKeyPress KeyEvents}, we have one more option, {@link ToolManager.sharedKeyEvents sharedKeyEvents}.
 *
 * {@link ToolManager} hands out the event to {@link EventHandler}.\
 * {@link EventHandler} inspects if it can handle the event and what to do with it.\
 * {@link EventHandler} then returns a {@link EventHandlerResult response} to {@link ToolManager} telling how to proceed:
 *
 * {@link EventHandler} may have started a new {@link ToolAction action}, in which case {@link ToolManager} should set that action as {@link ToolManager.activeAction active}.\
 * {@link EventHandler} may have ended an {@link ToolAction action}, in which case the {@link ToolManager}'s {@link ToolManager.activeAction active action} should be removed.\
 * {@link EventHandler} also informs whether the event was successfully handled or not,\
 * and whether the event should be propagated further or not.
 *
 * {@link ToolManager} then either proceeds the propagation further or exits the event handling.
 */
export class ToolManager {
    /**
     * {@link ToolManager} singleton. There may only ever be one active ToolManager at any given moment.
     */
    private static readonly toolManager = new ToolManager();

    /**
     * {@link EventEmitter} to emit events.
     * This is used to emit events that are not directly related to the {@link ToolManager}.
     * Such as the {@link ViewManager.viewUpdateEvent viewUpdateEvent}.
     */
    private static eventEmitter = new EventEmitter();

    /**
     * The currently active Tool.\
     * This is used in event propagation.
     */
    private activeTool?: Tool;

    private constructor() {}

    /**
     * Method to handle the EventHandlerResult.
     * @param result EventHandlerResult that should be handled
     * @returns true if the event should stop propagating, false to continue propagation further.
     */
    private handleEventResult(result: EventHandlerResult): boolean {
        if (result.viewUpdateEvent) {
            ViewManager.viewUpdateEvent(result.viewUpdateEvent);
        }
        return result.stopPropagation;
    }

    /**
     * Method to create the brush tool.
     * @param structure The structure that is selected
     * @param image The image that is selected
     * @param panZoom The panZoom that is selected
     * @param brushOptions The options for the brush tool
     * @param updateBrushSizeCallback The callback function to update the brush size
     * @returns The brush tool that is created
     */
    private createBrushTool = (
        structure: RegionOfInterest | undefined,
        image: ImageObject | undefined,
        panZoom: PanZoom,
        brushOptions: BrushOptions | LineOptions,
        updateBrushSizeCallback: (size: number) => void
    ) => {
        if (!structure || !image) return null;

        switch (brushOptions.mode) {
            case 'static':
                return new StaticBrushTool(
                    image,
                    structure,
                    panZoom,
                    brushOptions.erase,
                    brushOptions.diameter,
                    updateBrushSizeCallback
                );
            case 'smart':
                return new AdaptiveBrushTool(
                    image,
                    structure,
                    panZoom,
                    brushOptions.erase,
                    brushOptions.diameter,
                    updateBrushSizeCallback
                );
            default:
                return null;
        }
    };

    /**
     * Method to create the line tool.
     * @param structure The structure that is selected
     * @param image The image that is selected
     * @param panZoom The panZoom that is selected
     * @param view The transversal view that is selected
     * @param option The options for the line tool
     * @returns The line tool that is created
     */
    private createLineTool = (
        structure: RegionOfInterest | undefined,
        image: ImageObject | undefined,
        panZoom: PanZoom,
        view: PlaneView | undefined,
        option: BrushOptions | LineOptions
    ) => {
        if (!structure || !image || !view) return null;
        if (option.mode === 'draw' || option.mode === 'erase') {
            return new LineDrawTool(
                image,
                structure,
                panZoom,
                option.mode,
                view
            );
        }
        return null;
    };

    /**
     * Method to get the tool for the selected tool.
     * @param selectedTool The tool that is selected
     * @param structure The structure that is selected
     * @param image The image that is selected
     * @param panZoom The panZoom that is selected
     * @param view The transversal view that is selected
     * @returns The tool that is selected
     */
    private getToolForSelection = (
        selectedTool: string,
        structure: RegionOfInterest | undefined,
        image: ImageObject | undefined,
        panZoom: PanZoom,
        view: PlaneView | undefined,
        contouringToolOptions?: BrushOptions | LineOptions | undefined,
        contouringToolCallbackFunction?: (size: number) => void,
        viewManager?: ViewManager
    ) => {
        switch (selectedTool) {
            case 'pan':
            default:
                return new PanTool(panZoom);
            case 'window':
                return viewManager && new WindowingTool(viewManager);
            case 'brush':
                return (
                    contouringToolOptions &&
                    contouringToolCallbackFunction &&
                    this.createBrushTool(
                        structure,
                        image,
                        panZoom,
                        contouringToolOptions,
                        contouringToolCallbackFunction
                    )
                );
            case 'line':
                return (
                    contouringToolOptions &&
                    this.createLineTool(
                        structure,
                        image,
                        panZoom,
                        view,
                        contouringToolOptions
                    )
                );
        }
    };

    /**
     * Method to set the {@link ToolManager.activeTool activeTool}.\
     * This first calls {@link ToolManager.endCurrentAction} to finish the {@link ToolManager.activeAction activeAction}.\
     * {@link ToolManager.activeTool activeTool} is the currently active tool,\
     * which receives events and performs actions accordingly.
     * @param tool {@link Tool} that is set active.
     * @returns Reference to {@link ToolManager this}.
     */
    public static setActiveTool(
        selectedTool: string,
        structure: RegionOfInterest | undefined,
        image: ImageObject | undefined,
        panZoom: PanZoom,
        view: PlaneView | undefined,
        contouringToolOptions?: BrushOptions | LineOptions | undefined,
        contouringToolCallbackFunction?: (size: number) => void,
        viewManager?: ViewManager
    ) {
        const newActiveTool = this.toolManager.getToolForSelection(
            selectedTool,
            structure,
            image,
            panZoom,
            view,
            contouringToolOptions,
            contouringToolCallbackFunction,
            viewManager
        );
        if (newActiveTool) {
            this.deactivateActiveTool();
            this.toolManager.activeTool = newActiveTool;
        }
    }

    /**
     * Method to deactivate the active tool
     */
    private static deactivateActiveTool() {
        const result = this.toolManager.activeTool?.onDeactivate();
        if (result) {
            this.toolManager.handleEventResult(result);
        }
    }

    /**
     * Method to get the active tool
     */
    public static getActiveTool(): Tool | undefined {
        return this.toolManager.activeTool;
    }

    /**
     * Method to get the {@link ToolManager.eventEmitter eventEmitter}.\
     */
    public static getEventEmitter(): EventEmitter {
        return this.eventEmitter;
    }

    /**
     * {@link ToolManager.onKeyPress KeyEvents} that are not subjective to specific {@link ToolAction}, {@link Tool} or {@link View}.\
     * Such as undo (Ctrl+Z) and redo (Ctrl + Y).
     * @param key The key that was pressed
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the key press
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED}, if the event couldn't be handled
     * {@link EventHandlerResult} of any other type if the event was succesfully handled.
     */
    private sharedKeyEvents(
        key: string,
        modifierKeys: ModifierKeys
    ): EventHandlerResult {
        switch (key) {
            case 'z':
                if (modifierKeys.equals(ModifierKeys.CTRL)) {
                    //TODO: To be reintroduced
                    /*if(StateManager.undo()){
                        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
                    }*/
                }
                break;
            case 'y':
                if (modifierKeys.equals(ModifierKeys.CTRL)) {
                    //TODO: To be reintroduced
                    /*if(StateManager.redo()){
                        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
                    }*/
                }
                break;
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Mouse press event triggered by {@link ViewManager}.
     * @param button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public static onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: vec2,
        view: View
    ): boolean {
        const manager = this.toolManager;
        this.eventEmitter.emit('onMousePress', {
            button,
            modifierKeys,
            coordinates,
            view,
        });
        //if it fails,
        if (manager.activeTool) {
            //let the active tool handle it
            if (
                manager.handleEventResult(
                    manager.activeTool.onMousePress(
                        button,
                        modifierKeys,
                        coordinates,
                        view
                    )
                )
            ) {
                return true;
            }
        }
        //if that fails also,
        if (view && view.onMousePress) {
            //let the view try to handle it
            return manager.handleEventResult(
                view.onMousePress(button, modifierKeys, coordinates, view)
            );
        }
        //if none of  is able to handle the event, we just return false to inform that the event couldn't be handled.
        return false;
    }

    /**
     * Mouse move event triggered by {@link ViewManager}.
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public static onMouseMove(
        coordinates: vec2,
        buttons: number,
        view: View
    ): boolean {
        const manager = this.toolManager;
        if (manager.activeTool) {
            if (
                manager.handleEventResult(
                    manager.activeTool.onMouseMove(coordinates, buttons, view)
                )
            ) {
                return true;
            }
        }
        if (view.onMouseMove) {
            return manager.handleEventResult(
                view.onMouseMove(coordinates, buttons, view)
            );
        }
        return false;
    }

    /**
     * Mouse release event triggered by {@link ViewManager}.
     * @param button The button that triggered the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public static onMouseRelease(
        button: number,
        coordinates: vec2,
        view: View
    ): boolean {
        const manager = this.toolManager;
        this.eventEmitter.emit('onMouseRelease', { button, coordinates, view });
        if (manager.activeTool) {
            if (
                manager.handleEventResult(
                    manager.activeTool.onMouseRelease(button, coordinates, view)
                )
            ) {
                return true;
            }
        }
        if (view && view.onMouseRelease) {
            return manager.handleEventResult(
                view.onMouseRelease(button, coordinates)
            );
        }
        return false;
    }

    /**
     * Mouse wheel event triggered by {@link ViewManager}.
     * @param delta The direction of the MouseWheel event.\
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public static onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: vec2,
        view: PlaneView
    ): boolean {
        const manager = this.toolManager;
        if (manager.activeTool) {
            if (
                manager.handleEventResult(
                    manager.activeTool.onMouseWheel(
                        delta,
                        modifierKeys,
                        coordinates,
                        view
                    )
                )
            ) {
                return true;
            }
        }
        if (view && view.onMouseWheel) {
            const result = view.onMouseWheel(
                delta,
                modifierKeys,
                coordinates,
                view
            );
            // a quick workaround to updating tool to new coordinates
            if (manager.activeTool) {
                manager.activeTool.onMouseMove(coordinates, 0, view);
            }
            return manager.handleEventResult(result);
        }
        return false;
    }

    /**
     * Mouse double click event triggered by {@link ViewManager}.
     */
    public static onDoubleClick(coordinates: vec2, view: PlaneView): boolean {
        const manager = this.toolManager;
        if (manager.activeTool) {
            if (
                manager.handleEventResult(
                    manager.activeTool.onMouseDoubleClick(coordinates, view)
                )
            ) {
                return true;
            }
        }
        return false;
    }

    public static onMouseLeave() {
        this.deactivateActiveTool();
    }

    /**
     * Key press event triggered by {@link ViewManager}.
     * @param key The key that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool}, {@link view} or {@link ToolManager.sharedKeyEvents sharedKeyEvents}.\
     * False otherwise.
     */
    public static onKeyPress(
        key: string,
        modifierKeys: ModifierKeys,
        view: View
    ): boolean {
        const manager = this.toolManager;
        if (manager.activeTool) {
            if (
                manager.handleEventResult(
                    manager.activeTool.onKeyPress(key, modifierKeys, view)
                )
            ) {
                return true;
            }
        }
        if (view && view.onKeyPress) {
            if (manager.handleEventResult(view.onKeyPress(key, modifierKeys))) {
                return true;
            }
        }
        return manager.handleEventResult(
            manager.sharedKeyEvents(key, modifierKeys)
        );
    }
}
